<!--
  - Copyright (c) 2022 by Leyton Group - All Rights Reserved.
  - This source code, and resulting software, is the confidential and proprietary information
  - ("Proprietary Information") and is the intellectual property ("Intellectual Property")
  - of Leyton Group, Inc. ("The Company"). You shall not disclose such Proprietary Information
  - and shall use it only in accordance with the terms and conditions of any and all license
  - agreements you have entered into with The Company.
  -->

<template>
  <b-modal
    :id="id"
    modal-class="giga-modal"
    dialog-class="modal-lg"
    content-class="text-center pb-2"
    no-close-on-esc
    no-close-on-backdrop
    hide-header
    hide-footer
    lazy
    @show="initializeDataModal"
  >
    <div class="gigawhat-modal">
      <div class="modal-heading">
        <h2 class="modal-title" v-text="title"></h2>
        <p class="modal-description" v-text="description"></p>
      </div>

      <div class="giga-fields">
        <div class="giga-input_field half-field" v-if="isB2B2B">
          <label class="input-field_label">{{ $t("choose_the_agency") }}</label>
          <div class="input-field_area">
            <select
              id="agence"
              disabled
              v-model="$v.assignUserForm.agence.$model"
              class="small-input"
              @change="onChangeAgence($v.assignUserForm.agence.$model)"
              style="width: 300px;"
              :class="{ 'field-hasError': $v.assignUserForm.agence.$invalid }"
            >
              <option v-if="userInfo.admin" :value="-1">AUCUNE AGENCE</option>
              <option
                v-for="agence of agences"
                :key="agence.id"
                :value="agence"
                >{{ agence.raisonSocial }}</option
              >
            </select>
            <div
              class="validation-field-holder"
              v-if="
                $v.assignUserForm.agence.$anyDirty &&
                  $v.assignUserForm.agence.$invalid
              "
            >
              <small
                v-if="!$v.assignUserForm.agence.required"
                class="form-text text-danger"
              >
                {{ $t("required_field") }}
              </small>
            </div>
          </div>
        </div>
        <div class="giga-input_field" :class="computedUserWidth">
          <label class="input-field_label">{{ $t("choose_the_user") }}</label>
          <div class="input-field_area">
            <select
              id="user"
              v-model="$v.assignUserForm.user.$model"
              class="small-input"
              :style="customUserSelectStyle"
              :class="{ 'field-hasError': $v.assignUserForm.user.$invalid }"
            >
              <option v-for="user of users" :key="user.id" :value="user">{{
                user.email.split("@")[0]
              }}</option>
            </select>
            <div
              class="validation-field-holder"
              v-if="
                $v.assignUserForm.user.$anyDirty &&
                  $v.assignUserForm.user.$invalid
              "
            >
              <small
                v-if="!$v.assignUserForm.user.required"
                class="form-text text-danger"
              >
                {{ $t("required_field") }}
              </small>
            </div>
          </div>
        </div>
      </div>

      <div class="gigwhat-model_footer mt-5">
        <div class="half-footer left-content">
          <div class="giga-button">
            <button
              class="giga-blue_outlined_button no-icon"
              @click="closeAssignUserModal()"
            >
              Quitter
            </button>
          </div>
        </div>
        <div class="half-footer right-content">
          <div class="giga-button">
            <button
              :disabled="$v.assignUserForm.$invalid"
              class="giga-green_button no-icon"
              @click="assignUser()"
            >
              Terminer
            </button>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import { required, requiredIf } from "vuelidate/lib/validators";
import { constants } from "@/shared/constants";
import { agenceService } from "@/views/service/agence.service";
import { userService } from "@/views/service/user.service";
import { helpers } from "@/shared/helper";
import store from "@/store";

export default {
  name: "AssignUserModal",
  props: [
    "id",
    "title",
    "description",
    "userToDisable",
    "assignUserForm",
    "loader"
  ],
  components: {},
  assignUserForm: {
    agence: {},
    user: {}
  },
  data() {
    return {
      agences: [],
      users: [],
      USER_TYPE: constants.USER_TYPE,
      ROLE_TYPE: constants.ROLE_TYPE
    };
  },
  validations: {
    assignUserForm: {
      agence: {
        required: requiredIf(function() {
          return this.isB2B2B;
        })
      },
      user: {
        required
      }
    }
  },
  computed: {
    ...mapGetters("user", {
      currentTab: "getCurrentTab",
      isAffected: "getAffected"
    }),
    ...mapGetters("account", { userInfo: "getCurrentUser" }),
    computedUserWidth() {
      return this.isB2B2B ? "col-lg-6 half-field" : "col-lg-12";
    },
    customUserSelectStyle() {
      return {
        width: this.isB2B2B ? "300px" : "90%"
      };
    },
    isNotB2B2B() {
      return helpers.isNotB2B2B();
    },
    isB2B2B() {
      return this.userInfo.clientType === this.USER_TYPE.B2B2B;
    }
  },
  mounted() {
    this.$root.$on("bv::modal::show", (bvEvent, modalId) => {
      this.$v.$reset();
    });
    this.$v.assignUserForm.agence.$model = null;
    this.$v.assignUserForm.user.$model = null;
    this.fetchAgences();
  },
  methods: {
    assignUser() {
      this.$emit("confirmAssignModalClick");
    },
    closeAssignUserModal() {
      store.commit("user/setAffected", false);
      this.$emit("closeAssignModalClick");
    },
    fetchAgences() {
      agenceService.allUserAgencesList().then(res => {
        this.agences = res.content;
      });
    },
    async onChangeAgence(agency) {
      if (agency === -1) {
        const result = await this.fetchUsersByAgency(agency);
        this.$v.assignUserForm.user.$model = result.find(
          user => user.email === this.userInfo.email
        );
      } else {
        this.$v.assignUserForm.user.$model = null;
        this.fetchUsersByAgency(agency);
      }
    },
    async fetchUsersByAgency(agency) {
      try {
        var users = await userService.agenceUsersList(
          agency === -1 ? agency : agency.id
        );
        this.users = users.filter(user => user.id !== this.userToDisable?.id);
        return users;
      } catch (error) {
        console.error(error);
      }
    },
    async initializeDataModal() {
      if (this.isB2B2B) {
        if (this.userInfo.admin) {
          this.$v.assignUserForm.agence.$model = this.userToDisable?.agence;
          this.onChangeAgence(this.userToDisable?.agence);
        }
        if (this.userInfo.role?.roleType === this.ROLE_TYPE.ADMIN_AGENCE) {
          this.$v.assignUserForm.agence.$model = this.userInfo.agence;
          await this.fetchUsersByAgency(this.$v.assignUserForm.agence.$model);
          this.$v.assignUserForm.user.$model = this.users.find(
            user => user.email === this.userInfo.email
          );
        }
      } else if (this.isNotB2B2B) {
        this.fetchUsers();
      }
    },
    fetchUsers() {
      userService.clientUsersByRole().then(users => {
        this.users = users.filter(user => user.id !== this.userToDisable?.id);
      });
    }
  }
};
</script>

<style scoped>
.field-hasError {
  box-shadow: 0px 0px 3px 0px #e90e00 !important;
}
</style>
